import moment from 'moment-timezone';
import { LegFiJwtService } from '../../services/auth/legfi-jwt.service';
import { Note } from './note';
import { PaymentAttempt } from './payment-attempt';
import { Membership } from './membership';
import { Organization } from './organization';
import { Site } from './site';
import { User } from './user';
import { Owner } from './owner';
import { Unit } from './unit';
import { PaymentItem } from 'app/models/entities/payment-item';
import { AccountCredit } from 'app/models/entities/account-credit';
import { Charge } from 'app/models/entities/charge';
import { LedgerEntry } from './ledger-entry';
import Moment = moment.Moment;

export class PaymentGroup
{
    id: number;
    organizationId: number;
    payorId: number;
    payorType: string;
    paymentMethodType: string;
    paymentMethodValue: string;
    net: number;
    applied: number;
    gross: number;
    currency: string;
    autopaid: boolean;
    createdAt: Moment;
    updatedAt: Moment;
    importedAt: Moment;

    organization: Organization;
    payor: Membership | Organization | Site | Unit | User | Owner;
    notes: Note[] = [];
    paymentAttempts: PaymentAttempt[] = [];
    ledgerEntries: LedgerEntry[] = [];
    // SD-8153: the reversing entries posted when this payment was returned. They
    // reference the returned-payment Transaction (not the PaymentGroup), so the
    // backend surfaces them separately from `ledgerEntries` (the original recording).
    returnLedgerEntries: LedgerEntry[] = [];

    // Appends
    absorbedFees: number = 0;
    offloadedFees: number = 0;
    escrow: number = 0;
    processor: string;
    hasValidPayments: boolean;

    checked: boolean;
    hidden: boolean;

    deletable = false;
    paymentThatBoughtCredit: number;
    paymentItemSum: number;
    depositDate: Moment = null;
    paymentAttemptsSum: number;

    linkingBatchId: string | null = null;

    createdBy: User;
    deletedBy: User;

    paymentMethodDisplayName: string = '';
    paymentMethodLast4: string = '';

    constructor(paymentGroup: any) {
        if (paymentGroup.id) {
            this.id = paymentGroup.id;
        }
        if (paymentGroup.organizationId) {
            this.organizationId = paymentGroup.organizationId;
        }
        if (paymentGroup.payorId) {
            this.payorId = paymentGroup.payorId;
        }
        if (paymentGroup.payorType) {
            this.payorType = paymentGroup.payorType;
        }
        if (paymentGroup.paymentMethodType) {
            this.paymentMethodType = paymentGroup.paymentMethodType;
        }
        if (paymentGroup.paymentMethodValue) {
            this.paymentMethodValue = paymentGroup.paymentMethodValue;
        }
        if (paymentGroup.net !== null) {
            this.net = paymentGroup.net;
        }
        if (paymentGroup.applied !== null) {
            this.applied = paymentGroup.applied;
        }
        if (paymentGroup.gross !== null) {
            this.gross = paymentGroup.gross;
        }
        if (paymentGroup.currency) {
            this.currency = paymentGroup.currency.toLocaleUpperCase();
        }
        this.paymentThatBoughtCredit = paymentGroup.paymentThatBoughtCredit || null;

        const timezone = LegFiJwtService.getTimezone();
        if (paymentGroup.createdAt) {
            this.createdAt = moment.utc(paymentGroup.createdAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }
        if (paymentGroup.updatedAt) {
            this.updatedAt = moment.utc(paymentGroup.updatedAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }
        if (paymentGroup.importedAt) {
            this.importedAt = moment.utc(paymentGroup.importedAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }
        if (paymentGroup.depositDate) {
            this.depositDate = moment.utc(paymentGroup.depositDate, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }

        if (typeof paymentGroup.absorbedFees !== 'undefined' && paymentGroup.absorbedFees !== null) {
            this.absorbedFees = paymentGroup.absorbedFees;
        }
        if (typeof paymentGroup.offloadedFees !== 'undefined' && paymentGroup.offloadedFees !== null) {
            this.offloadedFees = paymentGroup.offloadedFees;
        }
        if (typeof paymentGroup.escrow !== 'undefined' && paymentGroup.escrow !== null) {
            this.escrow = paymentGroup.escrow;
        }
        if (paymentGroup.processor) {
            this.processor = paymentGroup.processor;
        }
        if (paymentGroup.hasValidPayments) {
            this.hasValidPayments = paymentGroup.hasValidPayments;
        }

        if (paymentGroup.organization) {
            this.organization = new Organization(paymentGroup.organization);
        }
        if (paymentGroup.payor && paymentGroup.payorType !== null) {
            if (paymentGroup.payorType.indexOf('Membership') !== -1) {
                this.payor = new Membership(paymentGroup.payor);
            } else if (paymentGroup.payorType.indexOf('Organization') !== -1) {
                this.payor = new Organization(paymentGroup.payor);
            } else if (paymentGroup.payorType.indexOf('Site') !== -1) {
                this.payor = new Site(paymentGroup.payor);
            } else if (paymentGroup.payorType.indexOf('User') !== -1) {
                this.payor = new User(paymentGroup.payor);
            } else if (paymentGroup.payorType.indexOf('Owner') !== -1) {
                this.payor = new Owner(paymentGroup.payor);
            } else if (paymentGroup.payorType.indexOf('Unit') !== -1) {
                this.payor = new Unit(paymentGroup.payor);
            }
        }
        if (paymentGroup.notes) {
            this.notes = paymentGroup.notes.map((note) => {
                return new Note(note);
            });
        }
        if (paymentGroup.paymentAttempts) {
            this.paymentAttempts = paymentGroup.paymentAttempts.map((paymentAttempt) => {
                this.autopaid = paymentAttempt.autopaid;
                return new PaymentAttempt(paymentAttempt);
            });
        }

        if (paymentGroup.ledgerEntries) {
            this.ledgerEntries = paymentGroup.ledgerEntries.map((entry) => new LedgerEntry(entry));
        }

        if (paymentGroup.returnLedgerEntries) {
            this.returnLedgerEntries = paymentGroup.returnLedgerEntries.map((entry) => new LedgerEntry(entry));
        }

        // SD-8328: `applied`/`net` are stored group columns that the Treasury refund flow (SD-8132)
        // does NOT reduce — only the legacy stripe path reduces the stored column. The refund getters
        // (isFullyRefunded / isPartiallyRefunded / originalApplied) and the sidenav Original/Net
        // breakdown read these, so normalize them net-of-refund by the Treasury-ONLY refunded total
        // summed across attempts (never the full refundedAmount — stripe already reduced the column,
        // which would double-subtract). Guarded: 0 Treasury refunds => byte-for-byte unchanged.
        const treasuryRefundedTotal = this.paymentAttempts.reduce((sum, pa) => sum + (pa.treasuryRefundedAmount ?? 0), 0);
        if (treasuryRefundedTotal > 0) {
            this.applied = Math.max(0, (this.applied ?? 0) - treasuryRefundedTotal);
            this.net = Math.max(0, (this.net ?? 0) - treasuryRefundedTotal);
        }

        this.paymentItemSum = this.paymentAttempts.flatMap(pa => pa.paymentItems ? pa.paymentItems : [])
                .map((pi: PaymentItem) => pi.amount)
                .reduce((a, b) => a + b, 0);

        this.paymentAttemptsSum =  this.paymentAttempts.map(pa => pa.net)
                .reduce((a, b) => a + b, 0);

        if (this.paymentAttempts.length > 0 && this.paymentAttempts[0].lastTransaction && this.paymentAttempts[0].lastTransaction.transaction) {
            this.depositDate = this.paymentAttempts[0].lastTransaction.transaction.transactionDate;
        }

        this.deletable = !this.isElectronicMethod;

        if (paymentGroup.linkingBatchId) {
            this.linkingBatchId = paymentGroup.linkingBatchId;
        }

        if (paymentGroup.createdBy) {
            this.createdBy = new User(paymentGroup.createdBy);
        }
        if (paymentGroup.deletedBy) {
            this.deletedBy = new User(paymentGroup.deletedBy);
        }

        if (paymentGroup.paymentMethodDisplayName) {
            this.paymentMethodDisplayName = paymentGroup.paymentMethodDisplayName;
        }
        if (paymentGroup.paymentMethodLast4) {
            this.paymentMethodLast4 = paymentGroup.paymentMethodLast4;
        }
    }

    get isLinked() {
        return this.paymentAttempts.map(pa => pa.transactionsCount).reduce((a, b) => a + b, 0) > 0;
    }

    // Every precondition PaymentGroupController::destroy enforces, in one place: the endpoint
    // reverses the payment's accounting and soft-deletes its attempts with no processor check
    // of its own, so each Delete surface (V1 row, V1 bulk, V2 row, V2 bulk, sidenav) has to
    // enforce this itself or offer a Delete that either cannot succeed or should never have
    // been offered. Write permission is not part of it — that is the caller's JWT, not the row.
    //
    // Linkage is scoped to LIVE attempts because destroy() iterates `paymentAttempts` (a plain
    // hasMany over a SoftDeletes model) and only tests those, while the payments list loads
    // attempts `withTrashed()` — so `isLinked`, which sums every attempt, would hide a Delete
    // the server would have allowed. Same live-attempt scoping the deposit-date column uses
    // (PaymentsTableResponseTransformer).
    //
    // Distinct from the `deletable` field above, which means only "not an electronic method"
    // and gates the V1 table's row checkboxes — those also feed Return and Archive, and
    // Archive needs already-deleted payments to stay selectable.
    get isDeletable(): boolean {
        return this.processor === 'offline'
                && !this.paymentAttempts[0]?.deletedAt
                && !this.paymentAttempts.some(pa => !pa.deletedAt && pa.transactionsCount > 0);
    }

    // SD-8153: a returned payment (bounced check / ACH return) — mirrors the backend
    // returned-payment signal (UnitAccountHistoryController): the first attempt is
    // flagged returned and carries no Stripe refunds (that would be a refund, not a
    // return). Distinct from `isFullyRefunded`, which is the electronic-refund path.
    get isReturned(): boolean {
        const first = this.paymentAttempts[0];
        return !!first?.returned && !first?.stripeRefunds?.length;
    }

    // The date the payment was marked returned (the attempt's soft-delete date, which
    // the account-history return row is also dated to). Null when not returned.
    get returnDate(): Moment {
        return this.paymentAttempts[0]?.deletedAt ?? null;
    }

    // SD-8153: on return the backend zeroes the group's gross/net/applied (it sums only
    // live attempts), so the payment detail modal showed $0. The soft-deleted attempt
    // still holds the original amount, so recover it from the attempts when returned.
    // For every non-returned payment this is byte-identical to `gross`/`net`.
    get displayGross(): number {
        return this.isReturned
            ? this.paymentAttempts.reduce((sum, pa) => sum + (pa.gross ?? 0), 0)
            : this.gross;
    }

    get displayNet(): number {
        return this.isReturned
            ? this.paymentAttempts.reduce((sum, pa) => sum + (pa.net ?? 0), 0)
            : this.net;
    }

    get refundedAmount(): number {
        return this.paymentAttempts.reduce((sum, pa) => sum + (pa.refundedAmount ?? 0), 0);
    }

    // Refunds that actually reduced net/applied — excludes fee-only refunds
    // (SD-7854), which only reduced gross. Use this when reconstructing the
    // original net/applied; use refundedAmount for payer-level totals (gross).
    get principalRefundedAmount(): number {
        return this.paymentAttempts.reduce((sum, pa) => sum + (pa.principalRefundedAmount ?? 0), 0);
    }

    get isPartiallyRefunded(): boolean {
        // Partial when refunds were issued but did not cover the full original
        // applied. Multi-PA splits (one PA fully refunded, another partial)
        // still register as partial here because the PG as a whole still has
        // money applied — we compare against originalApplied at the PG level.
        return this.refundedAmount > 0 && this.principalRefundedAmount < this.originalApplied;
    }

    get isElectronicMethod(): boolean {
        return this.paymentMethodType.indexOf('BankAccount') !== -1
                || this.paymentMethodType.indexOf('CreditCard') !== -1
                || this.paymentMethodType === 'CardToken';
    }

    get isFullyRefunded(): boolean {
        if (this.principalRefundedAmount >= this.originalApplied && this.principalRefundedAmount > 0) {
            return true;
        }
        // Legacy full-refund cascade: an electronic PA was soft-deleted with no
        // StripeRefund row recorded. Treat as fully refunded only when no money
        // remains applied anywhere on the PG. Offline/mailed/credit methods are
        // never refunded — only deleted — so they must NOT hit this branch and
        // instead fall through to the 'Deleted' banner.
        return this.isElectronicMethod
                && this.paymentAttempts.some(pa => pa.deletedAt)
                && (this.applied ?? 0) === 0;
    }

    get originalApplied(): number {
        // Fee-only refunds never reduced applied, so only principal refunds
        // are added back (SD-7854).
        return (this.applied ?? 0) + this.principalRefundedAmount;
    }

    get originalGross(): number {
        // Gross WAS reduced by every refund (principal and fee-only alike),
        // so the full refunded amount restores it.
        return (this.gross ?? 0) + this.refundedAmount;
    }

    get originalNet(): number {
        // Fee-only refunds never reduced net, so only principal refunds are
        // added back (SD-7854).
        return (this.net ?? 0) + this.principalRefundedAmount;
    }

    get accountDisplay(): string {
        if (!this.paymentMethodDisplayName) {
            return '';
        }

        if (this.paymentMethodLast4) {
            return `${this.paymentMethodDisplayName} x${this.paymentMethodLast4}`;
        }

        return this.paymentMethodDisplayName;
    }

    get friendlyPaymentMethod() {
        if (this.paymentMethodType.indexOf('BankAccount') !== -1) {
            return 'Bank Account';
        }
        if (this.paymentMethodType.indexOf('CreditCard') !== -1) {
            return 'Credit Card';
        }
        if (this.paymentMethodType === 'CardToken') {
            return 'Credit Card';
        }
        if (this.paymentMethodType === 'OfflineCheck') {
            return 'Check (offline)';
        }
        if (this.paymentMethodType === 'OfflineCash') {
            return 'Cash (offline)';
        }
        if (this.paymentMethodType === 'AccountCredit') {
            return 'Account Credit';
        }
        if (this.paymentMethodType === 'Mailed') {
            return 'Mailed Payment';
        }
    }

    get payables() {
        return this.paymentAttempts.flatMap(pa => pa.paymentItems).map(pi => pi.payable);
    }

    get chargesPaid() {
        return this.paymentAttempts?.flatMap(pa => pa.paymentItems).filter(pi => pi.payable instanceof Charge);
    }

    get creditsPurchased() {
        return this.paymentAttempts?.flatMap(pa => pa.paymentItems).filter(pi => pi.payable instanceof AccountCredit);
    }

    get creditsTotal() {
        return this.creditsPurchased.map(pi => pi.amount).reduce((a, b) => a + b, 0);
    }

    get creditRemaining() {
        return this.creditsPurchased.map(pi => (<AccountCredit>pi.payable).remaining).reduce((a, b) => a + b, 0);
    }

    get creditUsages() {
        return this.creditsPurchased.map(pi => pi.payable).flatMap((ac: AccountCredit) => ac.paymentGroups);
    }
}
